import axios from 'axios'
import config from '@/config'
import api from '@/constants/api.urls'

import { toCamelCaseObject, toSnakeCaseString } from '@/utils/common'
import * as types from '@/store/types'

const timeout = 600000

const { CancelToken } = axios
const client = axios.create({ baseURL: config.baseURL, timeout })

const AVAILABLE_SETTINGS_MAP = {
  has_performance_classification: api.settingUpdate,
  has_global_blacklist_classification: api.settingUpdate,
  has_dns_classification: api.settingUpdate,
  has_ctr_classification: api.settingUpdate,

  has_junk_notification_limit: api.settingJunkNotification,
  junk_notification_limit: api.settingJunkNotificationLimit,
  has_enable_notifications: api.settingEnableNotifications,
  has_placements_autodisable: api.settingUpdate,
  has_networks_banned: api.settingNetworksBanned,
  has_mask_classification: api.settingUpdate
}

const apiService = {
  client,

  cancelableRequests: [],
  isCancel(t) {
    return axios.isCancel(t)
  },
  getCancelToken() {
    return new CancelToken((cancel) => {
      this.cancelableRequests.push(cancel)
    })
  },
  cancelRequests() {
    this.cancelableRequests.forEach(cancel => cancel('cancelled'))
    this.cancelableRequests = []
  },

  // accountSettings
  getAccountSettings() {
    return this.client.get(api.accountSettings).then(response => toCamelCaseObject(response))
  },
  changeAccountSettings({ setting, accountId, value }) {
    const settingKey = toSnakeCaseString(setting)
    const settingUrl = AVAILABLE_SETTINGS_MAP[settingKey]
    const settingFullUrl = `${settingUrl}${accountId}/`

    return this.client.put(settingFullUrl, { [settingKey]: value }).then(response => ({
      [setting]:
        setting === 'junkNotificationLimit' ? Number(response[settingKey]) : response[settingKey]
    }))
  },
  saveNotificationTools(params) {
    return this.client.post(api.notificationEmails, params).then(response => ({
      emailList: response
    }))
  },
  saveMasks(params) {
    return this.client.post(api.masks, params).then(response => ({
      masks: [...response]
    }))
  },

  // action log
  fetchActionLog(options) {
    return this.client
      .get(api.log, {
        params: options,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const totalPage = Math.ceil(response.count / options.limit)
        return { list: toCamelCaseObject(response.results), totalPage }
      })
  },
  fetchActionLogItem(id, options) {
    return this.client
      .get(`${api.log}${id}/`, {
        params: options,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const { action: type, criteria } = response
        const totalPage = Math.ceil(response.criteria_count / options.limit)
        const list = criteria ? toCamelCaseObject(criteria) : []
        return {
          list,
          type,
          totalPage
        }
      })
  },

  // adGroup
  fetchAds(options) {
    return this.client
      .get(api.ads, {
        params: options,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const isValidConversions = Object.keys(response).some(
          key => key === 'is_conversions_sync_valid'
        )
          ? response.is_conversions_sync_valid
          : true
        const totalPage = Math.ceil(response.count / options.limit)
        const list = toCamelCaseObject(response.data)
        const { cpl } = response
        return {
          isValidConversions,
          list,
          cpl,
          totalPage
        }
      })
  },
  changeAdsStatus({ ads, status }) {
    return this.client.post(api.setAdsStatus, { ads, status })
  },
  changeAdsStatusFromActionLog({ criterions }) {
    return this.client
      .post(api.changeAdsStatusFromActionLog, { criterion_ids: criterions })
      .then(response => toCamelCaseObject(response))
  },
  fetchExceptionAds(options) {
    return this.client
      .get(api.exceptionAds, {
        params: options,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const isValidConversions = Object.keys(response).some(
          key => key === 'is_conversions_sync_valid'
        )
          ? response.is_conversions_sync_valid
          : true
        const totalPage = Math.ceil(response.count / options.limit)
        const list = toCamelCaseObject(response.data)
        return {
          isValidConversions,
          list,
          totalPage
        }
      })
  },
  changeExceptionAds({ ads, during, action }) {
    const availableActions = { add: api.addExceptionAds, delete: api.deleteExceptionAds }
    const url = availableActions[action]
    return this.client
      .post(url, { entities: ads, during })
      .then(response => toCamelCaseObject(response))
  },

  // keywords
  fetchKeywords(options) {
    return this.client
      .get(api.keywords, {
        params: options,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const isValidConversions = Object.keys(response).some(
          key => key === 'is_conversions_sync_valid'
        )
          ? response.is_conversions_sync_valid
          : true
        const totalPage = Math.ceil(response.count / options.limit)
        const list = toCamelCaseObject(response.data)
        const { cpl } = response
        return {
          isValidConversions,
          list,
          cpl,
          totalPage
        }
      })
  },
  changeKeywordsStatus({ keywords, status }) {
    return this.client.post(api.setKeywordsStatus, { keywords, status })
  },
  changeKeywordsStatusFromActionLog({ criterions }) {
    return this.client
      .post(api.changeKeywordsStatusFromActionLog, { criterion_ids: criterions })
      .then(response => toCamelCaseObject(response))
  },
  fetchExceptionKeywords(options) {
    return this.client
      .get(api.exceptionKeywords, {
        params: options,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const isValidConversions = Object.keys(response).some(
          key => key === 'is_conversions_sync_valid'
        )
          ? response.is_conversions_sync_valid
          : true
        const totalPage = Math.ceil(response.count / options.limit)
        const list = toCamelCaseObject(response.data)
        return {
          isValidConversions,
          list,
          totalPage
        }
      })
  },
  changeExceptionKeywords({ keywords, during, action }) {
    const availableActions = { add: api.addExceptionKeywords, delete: api.deleteExceptionKeywords }
    const url = availableActions[action]
    return this.client
      .post(url, { entities: keywords, during })
      .then(response => toCamelCaseObject(response))
  },

  // placements
  fetchAllPlacements(params) {
    return this.client
      .get(api.placements, {
        params,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const isValidConversions = 'is_conversions_sync_valid' in response
          ? response.is_conversions_sync_valid
          : true
        const hasActiveCampaigns = 'has_active_campaigns' in response
          ? response.has_active_campaigns
          : true

        const conversionsSyncDate = response.conversions_sync_date

        const placements = {
          best: response.best,
          disputed: response.disputable,
          worst: response.worst
        }

        return {
          isValidConversions,
          hasActiveCampaigns,
          conversionsSyncDate,
          placements: toCamelCaseObject(placements)
        }
      })
  },

  fetchPlacementsByType(name, options) {
    const placementsTypes = {
      [types.WORST_PLACEMENTS]: api.worstPlacements,
      [types.BEST_PLACEMENTS]: api.bestPlacements,
      [types.DISPUTED_PLACEMENTS]: api.disputedPlacements
    }
    const placementsType = placementsTypes[name]

    return this.client
      .get(placementsType, {
        params: options,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const isValidConversions = 'is_conversions_sync_valid' in response
          ? response.is_conversions_sync_valid
          : true
        const { count } = response
        const totalPage = Math.ceil(count / options.limit)
        const list = toCamelCaseObject(response.results)
        return {
          isValidConversions,
          list,
          count,
          totalPage
        }
      })
  },
  fetchBlacklistPlacements(options) {
    return this.client
      .get(api.blackListPlacements, {
        params: options,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const totalPage = Math.ceil(response.count / options.limit)
        const list = toCamelCaseObject(response.results)
        return { list, totalPage }
      })
  },
  fetchWhitelistPlacements(options) {
    return this.client
      .get(api.whiteListPlacements, {
        params: options,
        cancelToken: this.getCancelToken()
      })
      .then((response) => {
        const totalPage = Math.ceil(response.count / options.limit)
        const list = toCamelCaseObject(response.results)
        return { list, totalPage }
      })
  },
  changeBlacklistPlacements({ ids, action }) {
    const availableActions = {
      ADD_WORST: api.addBlacklistPlacementsWorst,
      ADD_DISPUTED: api.addBlacklistPlacementsDisputed,
      DELETE: api.deleteBlacklistPlacements
    }
    const url = availableActions[action]
    return this.client.post(url, { placements: ids }).then(response => toCamelCaseObject(response))
  },
  changeWhitelistPlacements({ ids, action }) {
    const availableActions = {
      ADD: api.addWhitelistPlacementsById,
      DELETE: api.deleteWhitelistPlacements
    }
    const url = availableActions[action]
    return this.client
      .post(url, { placement_ids: ids })
      .then(response => toCamelCaseObject(response))
  },
  moveBlacklistPlacementsToWhitelist({ ids }) {
    return this.client.post(api.moveBlacklistPlacementsToWhitelist, { placement_ids: ids })
  },
  addPlacementsToBlacklistFromFile({ campaign, formData }) {
    return this.client
      .post(api.addBlacklistPlacementsByFile, { campaign, formData })
      .then(response => toCamelCaseObject(response))
  },
  addPlacementsToWhitelistFromFile({ formData }) {
    return this.client
      .post(api.addWhitelistPlacementsByFile, formData)
      .then(response => toCamelCaseObject(response))
  },
  addPlacementsToBlacklistManually({ campaign, domains }) {
    return this.client
      .post(api.addBlacklistPlacementsByDomain, { campaign, placements: domains })
      .then(response => toCamelCaseObject(response))
  },
  addPlacementsToWhitelistManually({ domains }) {
    return this.client
      .post(api.addWhitelistPlacementsByDomain, { domains })
      .then(response => toCamelCaseObject(response))
  },
  addAllPlacementsToWhitelist({ type, during }) {
    return this.client
      .post(api.addAllPlacementsToWhitelist, { quality_type: type, during })
      .then(response => toCamelCaseObject(response))
  },
  addAllPlacementsToBlacklist({ type, during }) {
    return this.client
      .post(api.addAllPlacementsToBlacklist, { quality_type: type, during })
      .then(response => toCamelCaseObject(response))
  },

  // campaigns
  fetchCampaigns() {
    return this.client.get(api.campaigns).then(response => toCamelCaseObject(response))
  },
  exportCampaignBlacklist(campaign) {
    return this.client.get(api.exportCampaignBlacklist, { params: { campaign }, responseType: 'blob' })
  },

  // session
  createSessionWithCode({ code }) {
    const headers = { Authorization: null }
    return this.client
      .post(api.yandexAuth, { code }, headers)
      .then(response => toCamelCaseObject(response))
  },
  createSessionForView() {
    const headers = { Authorization: null }
    return this.client.get(api.viewAuth, { headers, withCredentials: true })
  },
  refreshSession({ refresh }) {
    return this.client.post(api.refreshToken, { refresh })
  },
  getSessionAccount() {
    return this.client
      .get(api.userProfileInfoData)
      .then(response => (response ? toCamelCaseObject(response) : null))
  },
  getAccountList() {
    return this.client
      .get(api.accounts)
      .then(response => ({ accounts: response ? toCamelCaseObject(response) : [] }))
  },
  selectAccount({ userId, username, directLogin }) {
    return this.client.put(`${api.selectAccount}${userId}/`, {
      username,
      direct_login: directLogin
    })
  },

  // summary
  fetchSummary() {
    return this.client
      .get(api.summaryData, {
        cancelToken: this.getCancelToken()
      })
      .then(response => toCamelCaseObject(response))
  }
}

export default apiService
